/*
  Restaurant Halong: Eine Beilage gehört immer zu ihrem Gericht.

  Beilagen sind Warenkorb-Zeilen mit der Eigenschaft „Beilage für“ (Wok-Auswahl in
  wok-custom-script.js, Beilagen-Auswahl in custom-script.js). Neue Paare tragen dieselbe
  versteckte Kennung `_Gruppe`; ältere Paare ohne Kennung werden über den Produkttitel zugeordnet.

  Regeln:
  - Beilage-Menge = Menge des Gerichts (das Gericht führt).
  - Gericht weg → Beilage weg.
  - Beilage einer Gruppe weg → Gericht dieser Gruppe weg (nur zusammen bestellbar).

  Geprüft wird nach jeder Warenkorb-Änderung und noch einmal vor der Kasse
  (halong-abholzeit.js ruft ensure() auf).
*/
(() => {
  if (window.__halongBeilage) return;

  const KEY = 'Beilage für';
  const GROUP = '_Gruppe';
  const CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const root = () => window.Shopify?.routes?.root || '/';
  const realFetch = window.fetch ? window.fetch.bind(window) : null;

  const isAddon = (item) => Boolean(item.properties && item.properties[KEY]);
  const groupOf = (item) => (item.properties && item.properties[GROUP]) || '';

  /** Reine Planung: welche Zeilen auf welche Menge gesetzt werden müssen. */
  function plan(cart) {
    const items = (cart && cart.items) || [];
    const addons = items.filter(isAddon);
    const mains = items.filter((i) => !isAddon(i));
    const updates = {};
    let removedAddons = 0;
    let removedMains = 0;
    let adjusted = 0;

    // Gruppen: Beilage folgt der Menge des Gerichts derselben Gruppe.
    for (const a of addons.filter(groupOf)) {
      const target = mains.filter((m) => groupOf(m) === groupOf(a)).reduce((s, m) => s + m.quantity, 0);
      if (target !== a.quantity) {
        updates[a.key] = target;
        if (target === 0) removedAddons += 1; else adjusted += 1;
      }
    }
    for (const m of mains.filter(groupOf)) {
      if (!addons.some((a) => groupOf(a) === groupOf(m))) {
        updates[m.key] = 0;
        removedMains += 1;
      }
    }

    // Ältere Paare ohne Kennung: Beilagen dürfen die Menge passender Gerichte nicht übersteigen.
    const pool = new Map();
    for (const m of mains) {
      if (groupOf(m)) continue;
      pool.set(m.product_title, (pool.get(m.product_title) || 0) + m.quantity);
    }
    for (const a of addons) {
      if (groupOf(a)) continue;
      const ref = String(a.properties[KEY]);
      const title = [...pool.keys()]
        .filter((t) => ref === t || ref.startsWith(`${t},`))
        .sort((x, y) => y.length - x.length)[0];
      const free = title ? pool.get(title) : 0;
      const target = Math.min(a.quantity, free);
      if (title) pool.set(title, free - target);
      if (target !== a.quantity) {
        updates[a.key] = target;
        if (target === 0) removedAddons += 1; else adjusted += 1;
      }
    }

    return { updates, removedAddons, removedMains, adjusted, changed: Object.keys(updates).length > 0 };
  }

  function announce(result) {
    if (typeof document === 'undefined' || !document.body) return;
    let text = 'Die Beilage wurde an die Menge des Gerichts angepasst.';
    if (result.removedMains) text = 'Ein Gericht ohne seine Beilage wurde entfernt. Bitte bei Bedarf neu zusammenstellen.';
    else if (result.removedAddons) text = 'Eine Beilage ohne ihr Gericht wurde aus dem Warenkorb entfernt.';
    let box = document.getElementById('halong-beilage-hinweis');
    if (!box) {
      box = document.createElement('div');
      box.id = 'halong-beilage-hinweis';
      box.setAttribute('role', 'status');
      box.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;'
        + 'max-width:min(92vw,420px);padding:14px 18px;border-radius:8px;background:#1a1a1a;color:#fff;'
        + 'font-size:17px;line-height:1.4;box-shadow:0 4px 16px rgba(0,0,0,.2)';
      document.body.appendChild(box);
    }
    box.textContent = text;
    clearTimeout(announce.timer);
    announce.timer = setTimeout(() => box.remove(), 7000);
  }

  let running = null;
  let again = false;

  async function runOnce() {
    const cart = await realFetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const result = plan(cart);
    if (result.changed) {
      const res = await realFetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: result.updates }),
      });
      if (!res.ok) throw new Error(`cart/update ${res.status}`);
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      announce(result);
      // Die Warenkorbseite hört nicht auf cart:refresh (nur Drawer und Zähler): neu laden, Hinweis mitnehmen.
      if (onCartPage() && Date.now() - loadedAfterReload > RELOAD_PAUSE_MS) {
        try { sessionStorage.setItem(NOTE_KEY, JSON.stringify(result)); } catch (e) { /* ohne Speicher: nur neu laden */ }
        window.location.reload();
      }
    }
    return result;
  }

  const NOTE_KEY = 'halong-beilage-hinweis';
  const RELOAD_PAUSE_MS = 5000; // Schutz vor Endlos-Neuladen, falls die Korrektur nicht greift
  let loadedAfterReload = 0;
  const onCartPage = () => /\/cart\/?$/.test(window.location.pathname);

  /** Nach dem Neuladen der Warenkorbseite den Hinweis von vorher zeigen. */
  function announceStored() {
    let stored = null;
    try {
      stored = sessionStorage.getItem(NOTE_KEY);
      sessionStorage.removeItem(NOTE_KEY);
    } catch (e) { return; }
    if (!stored) return;
    loadedAfterReload = Date.now();
    try { announce(JSON.parse(stored)); } catch (e) { /* defekter Eintrag: ignorieren */ }
  }

  /** Prüft und korrigiert den Warenkorb. Parallele Aufrufe laufen nacheinander. */
  function sync() {
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      let result;
      let changed = false;
      do {
        again = false;
        result = await runOnce();
        changed = changed || result.changed;
      } while (again);
      return { ...result, changed };
    })().finally(() => { running = null; });
    return running;
  }

  /** Vor der Kasse: true, wenn der Warenkorb stimmt; false, wenn korrigiert werden musste. */
  async function ensure() {
    try {
      const result = await sync();
      return !result.changed;
    } catch (e) {
      console.error('halong-beilage:', e);
      return true; // Prüfung nicht erreichbar: Kasse nicht blockieren, Bon zeigt die Positionen.
    }
  }

  let timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => { sync().catch((e) => console.error('halong-beilage:', e)); }, 150);
  }

  window.__halongBeilage = { plan, sync, ensure, KEY, GROUP };

  if (!realFetch || typeof document === 'undefined') return;

  // Jede Warenkorb-Änderung des Themes (fetch) und der Add-on-Skripte (jQuery) löst eine Prüfung aus.
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const p = realFetch(input, init);
    if (CART_RE.test(url)) p.then(schedule, schedule);
    return p;
  };
  document.addEventListener('DOMContentLoaded', () => {
    announceStored();
    if (window.jQuery) {
      window.jQuery(document).ajaxComplete((_e, _xhr, settings) => {
        if (CART_RE.test((settings && settings.url) || '')) schedule();
      });
    }
    schedule();
  });
  window.addEventListener('pageshow', (e) => { if (e.persisted) schedule(); });
})();
